import { useState } from "react";
import { Link } from "react-router";
import { api } from "../api/client";
import { dateTime } from "../text";
import { useCampaign, useCampaignQuery } from "./context";
import { EntryView } from "./EntryPage";
import { ThreadPlay } from "./EntryPlay";
import { Markdown } from "./Markdown";
import { MaterialList } from "./material";
import { PartyCards } from "./PartyPage";
import { AttachmentButton, CampaignError, FieldValue, fieldText, SecretBadge, useAction, useEntries, useEntry, useSummary } from "./parts";
import {
  BeginSceneDialog,
  BeginSessionDialog,
  CloseSessionDialog,
  deadlineText,
  fieldOf,
  NoteDialog,
  realDate,
  SetDayDialog,
  SetPlaceDialog,
  useFieldLabel,
  useSituation,
} from "./play";
import type { CampaignEntryListItem, CampaignSituation, SituationSession, SituationThread } from "./types";

/**
 * The start of a campaign is its situation (stage 2): what runs now, the active scene with its material, game day and place, the
 * party, open threads by deadline, the last session, and below it the section „Stand“ and the latest changes. Players get their view.
 */
export function CampaignHome() {
  const campaign = useCampaign();
  const situation = useSituation();
  if (campaign.mode === "player") return <PlayerHome />;
  return (
    <div className="stack situation">
      {situation.error && <CampaignError error={situation.error} reload />}
      {situation.isLoading && <p className="muted">Lade …</p>}
      {situation.data && <Situation situation={situation.data} />}
      <CampaignState />
      <RecentChanges />
    </div>
  );
}

function Situation({ situation }: { situation: CampaignSituation }) {
  return (
    <>
      <DayAndPlace situation={situation} />
      <Adventures situation={situation} />
      <ActiveScene situation={situation} />
      <Sessions situation={situation} />
      <section className="card" aria-label={useCampaign().term("party")}>
        <PartyCards />
      </section>
      <Threads threads={situation.threads} />
    </>
  );
}

function DayAndPlace({ situation }: { situation: CampaignSituation }) {
  const campaign = useCampaign();
  const action = useAction();
  const [dialog, setDialog] = useState<"day" | "place" | null>(null);
  const day = campaign.term("day");
  return (
    <section className="card day-card" aria-label={day}>
      <div className="row">
        <div className="grow">
          <p className="day-date">{situation.date.de}</p>
          <p className="muted small">
            {situation.dated && (
              <>
                {day} {situation.day} ·{" "}
              </>
            )}
            {campaign.kindLabel("place")}: {situation.place ? <Link to={campaign.entryPath(situation.place.id)}>{situation.place.title}</Link> : "unbekannt"}
          </p>
        </div>
        <div className="actions">
          <button
            type="button"
            className="button secondary"
            disabled={action.busy}
            onClick={() => void action.run(() => campaign.apply({ type: "campaign.day", payload: { action: "advance", days: 1 } }))}
          >
            {day} +1
          </button>
          <button type="button" className="link" onClick={() => setDialog("day")}>
            {day} setzen
          </button>
          <button type="button" className="link" onClick={() => setDialog("place")}>
            Ort setzen
          </button>
        </div>
      </div>
      <CampaignError error={action.error} onClose={action.clear} />
      {dialog === "day" && <SetDayDialog onClose={() => setDialog(null)} />}
      {dialog === "place" && <SetPlaceDialog onClose={() => setDialog(null)} />}
    </section>
  );
}

function Adventures({ situation }: { situation: CampaignSituation }) {
  const campaign = useCampaign();
  const term = campaign.term("adventure");
  const goalLabel = useFieldLabel()("adventure", "goal");
  return (
    <section className="card" aria-label={term}>
      <div className="section-head">
        <h2>{term}</h2>
        <Link to={`/k/${campaign.id}/abenteuer`}>Alle</Link>
      </div>
      {situation.adventures.length === 0 ? (
        <p className="muted small">Gerade läuft nichts.</p>
      ) : (
        <ul className="plain-list">
          {situation.adventures.map((a) => (
            <li key={a.id}>
              <Link to={campaign.entryPath(a.id)}>{a.title}</Link> <SecretBadge visibility={a.visibility} />
              {fieldOf(a, "goal") != null && (
                <p className="small">
                  <span className="muted">{goalLabel}:</span> {fieldText(fieldOf(a, "goal"))}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ActiveScene({ situation }: { situation: CampaignSituation }) {
  const campaign = useCampaign();
  const action = useAction();
  const [beginning, setBeginning] = useState(false);
  const term = campaign.term("scene");
  const scene = situation.scene;
  const specs = campaign.kind("scene")?.fields ?? [];
  return (
    <section className="card scene-card" aria-label={term}>
      {scene && <p className="muted small eyebrow">{term} · aktiv</p>}
      <div className="section-head">
        <h2>{scene ? <Link to={campaign.entryPath(scene.id)}>{scene.title}</Link> : `${term}: gerade nichts aktiv`}</h2>
        <div className="actions">
          {scene && (
            <button type="button" className="button secondary" disabled={action.busy} onClick={() => void action.run(() => campaign.apply({ type: "campaign.scene.end", payload: {} }))}>
              {term} beenden
            </button>
          )}
          <button type="button" className={`button ${scene ? "secondary" : "primary"}`} onClick={() => setBeginning(true)}>
            {scene ? "Wechseln" : `${term} beginnen`}
          </button>
        </div>
      </div>
      {scene && (
        <>
          {scene.fields
            .filter((f) => f.key !== "material" && f.value != null && f.type !== "references")
            .map((f) => (
              <p key={f.key} className="small">
                <span className="muted">{f.label.de}:</span> <FieldValue field={f} spec={specs.find((s) => s.key === f.key)} />
              </p>
            ))}
          {campaign.mode === "normal" && campaign.head.settings.gameMasterViaMcp ? (
            <p className="muted small">{campaign.term("gameMaster")} über MCP: Material gibt es hier nicht.</p>
          ) : (
            <MaterialList material={scene.material} sceneId={scene.id} />
          )}
        </>
      )}
      <CampaignError error={action.error} onClose={action.clear} />
      {beginning && <BeginSceneDialog onClose={() => setBeginning(false)} />}
    </section>
  );
}

function Sessions({ situation }: { situation: CampaignSituation }) {
  const campaign = useCampaign();
  const [dialog, setDialog] = useState<"begin" | "note" | "close" | null>(null);
  const term = campaign.term("session");
  const running = situation.session;
  return (
    <section className="card" aria-label={term}>
      <div className="section-head">
        <h2>{term}</h2>
        <Link to={`/k/${campaign.id}/sitzungen`}>Alle</Link>
      </div>
      {running ? (
        <>
          <p>
            Läuft: <Link to={campaign.entryPath(running.id)}>{running.title}</Link> <span className="muted small">· {realDate(running.date)}</span>
          </p>
          <div className="actions">
            <button type="button" className="button secondary" onClick={() => setDialog("note")}>
              Notiz
            </button>
            <button type="button" className="button secondary" onClick={() => setDialog("close")}>
              {term} abschließen
            </button>
          </div>
        </>
      ) : (
        <div className="actions">
          <button type="button" className="button primary" onClick={() => setDialog("begin")}>
            {term} beginnen
          </button>
        </div>
      )}
      {situation.lastSession && <LastSession session={situation.lastSession} />}
      {dialog === "begin" && <BeginSessionDialog onClose={() => setDialog(null)} />}
      {dialog === "note" && <NoteDialog onClose={() => setDialog(null)} />}
      {dialog === "close" && running && <CloseSessionDialog sessionId={running.id} title={running.title} onClose={() => setDialog(null)} />}
    </section>
  );
}

function LastSession({ session }: { session: SituationSession }) {
  const campaign = useCampaign();
  return (
    <div className="last-session">
      <h3>
        Zuletzt: <Link to={campaign.entryPath(session.id)}>{session.title}</Link> <span className="muted small">· {realDate(session.date)}</span>
      </h3>
      {session.summary?.trim() ? <Markdown text={session.summary} references={[]} entryPath={campaign.entryPath} /> : <p className="muted small">Noch ohne Zusammenfassung.</p>}
    </div>
  );
}

function Threads({ threads }: { threads: SituationThread[] }) {
  const campaign = useCampaign();
  const term = campaign.kindLabel("thread");
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section className="card" aria-label={`${term}: offen`}>
      <h2>{term}: offen</h2>
      {threads.length === 0 ? (
        <p className="muted small">Keine offenen.</p>
      ) : (
        <ul className="plain-list" aria-label={`${term}: offen`}>
          {threads.map((t) => (
            <li key={t.id} className="stack">
              <div className="row">
                <span className="grow">
                  <Link to={campaign.entryPath(t.id)}>{t.title}</Link>
                  {t.deadline && (
                    <span className={`small${t.deadline.offset < 0 ? " error" : " muted"}`}>
                      {" "}
                      · Frist {deadlineText(t.deadline.offset)} ({t.deadline.date.de})
                    </span>
                  )}
                </span>
                <button type="button" className="link" aria-expanded={open === t.id} onClick={() => setOpen(open === t.id ? null : t.id)}>
                  Ändern
                </button>
              </div>
              {open === t.id && <ThreadPlay id={t.id} status={t.status ?? null} deadline={t.deadline ?? null} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The entry of the kind campaign, with its query, so a failed load shows as an error and not as endless loading. */
function useCampaignEntry() {
  const entries = useEntries({ kind: "campaign" });
  return { id: entries.data?.[0]?.id ?? null, query: entries };
}

function CampaignState() {
  const { id, query } = useCampaignEntry();
  if (query.error) return <CampaignError error={query.error} reload />;
  if (query.isLoading) return <p className="muted">Lade …</p>;
  return id ? <CampaignEntry entryId={id} /> : <p className="muted">Noch kein Stand.</p>;
}

function CampaignEntry({ entryId }: { entryId: string }) {
  const entry = useEntry(entryId);
  if (entry.error) return <CampaignError error={entry.error} reload />;
  if (!entry.data) return <p className="muted">Lade …</p>;
  return <EntryView entry={entry.data} embedded />;
}

function RecentChanges() {
  const campaign = useCampaign();
  const log = useCampaignQuery(["log"], (headers) => api.GET("/api/campaigns/{id}/log", { params: { path: { id: campaign.id } }, headers }));
  const recent = (log.data ?? []).filter((e) => e.status === "active").slice(0, 8);
  return (
    <section className="card">
      <div className="section-head">
        <h2>Letzte Änderungen</h2>
        <Link to={`/k/${campaign.id}/protokoll`}>Protokoll</Link>
      </div>
      {log.error ? (
        <CampaignError error={log.error} reload />
      ) : log.isLoading ? (
        <p className="muted small">Lade …</p>
      ) : recent.length === 0 ? (
        <p className="muted small">Noch keine.</p>
      ) : (
        <ul className="plain-list" aria-label="Letzte Änderungen">
          {recent.map((e) => (
            <li key={e.revision}>
              {e.summary} <span className="muted small">· {dateTime(e.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * What the players see, in large type and without the words of the game master: the day, where the party is, what it is doing,
 * the state of the campaign, known persons and places, open threads, handouts and the summaries of the sessions.
 */
function PlayerHome() {
  const campaign = useCampaign();
  const entries = useEntries();
  const situation = useSituation();
  const campaignEntryId = useCampaignEntry().id;
  const all = entries.data ?? [];
  const of = (kind: string) => all.filter((e) => e.kind === kind);
  const openThreads = of("thread").filter((e) => (fieldOf(e, "status") ?? "open") === "open");
  const handouts = useCampaignQuery(["handouts"], (headers) => api.GET("/api/campaigns/{id}/attachments", { params: { path: { id: campaign.id } }, headers }));
  const now = situation.data;
  return (
    <div className="stack player-home">
      {entries.error && <CampaignError error={entries.error} reload />}
      {now && (
        <section className="card day-card" aria-label="Heute">
          <p className="day-date">{now.date.de}</p>
          {now.place && (
            <p>
              Wo: <Link to={campaign.entryPath(now.place.id)}>{now.place.title}</Link>
            </p>
          )}
          {now.adventures.map((a) => (
            <p key={a.id}>
              {campaign.term("adventure")}: <Link to={campaign.entryPath(a.id)}>{a.title}</Link>
            </p>
          ))}
        </section>
      )}
      {campaignEntryId && <PlayerState entryId={campaignEntryId} />}
      <PlayerList title={campaign.kindLabel("person")} entries={of("person")} />
      <PlayerList title={campaign.kindLabel("place")} entries={of("place")} />
      <PlayerList title={`${campaign.kindLabel("thread")}: offen`} entries={openThreads} />
      <section className="card">
        <h2>Handouts</h2>
        {handouts.error ? (
          <CampaignError error={handouts.error} reload />
        ) : (handouts.data ?? []).length === 0 ? (
          <p className="muted">Noch keine.</p>
        ) : (
          <ul className="plain-list" aria-label="Handouts">
            {handouts.data?.map((h) => (
              <li key={h.attachment.id}>
                <AttachmentButton attachment={h.attachment} /> <span className="muted">· {h.title}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="card">
        <h2>{campaign.kindLabel("session")}</h2>
        {of("session").length === 0 ? (
          <p className="muted">Noch keine.</p>
        ) : (
          of("session").map((s) => <SessionSummary key={s.id} session={s} />)
        )}
      </section>
    </div>
  );
}

function PlayerState({ entryId }: { entryId: string }) {
  const campaign = useCampaign();
  const entry = useEntry(entryId);
  const state = entry.data?.sections.find((s) => s.key === "state");
  if (!state || !state.text.trim()) return null;
  return (
    <section className="card">
      <h2>{state.heading}</h2>
      <Markdown text={state.text} references={state.references} entryPath={campaign.entryPath} />
    </section>
  );
}

function PlayerList({ title, entries }: { title: string; entries: CampaignEntryListItem[] }) {
  const campaign = useCampaign();
  return (
    <section className="card">
      <h2>{title}</h2>
      {entries.length === 0 ? (
        <p className="muted">Noch keine.</p>
      ) : (
        <ul className="plain-list" aria-label={title}>
          {entries.map((e) => {
            const summary = e.fields.find((f) => f.type === "text" && f.value != null);
            return (
              <li key={e.id}>
                <Link to={campaign.entryPath(e.id)}>{e.title}</Link>
                {summary && <span className="muted"> · {fieldText(summary.value)}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function SessionSummary({ session }: { session: CampaignEntryListItem }) {
  const campaign = useCampaign();
  const summary = useSummary(session.id).section;
  return (
    <article className="session-summary">
      <h3>
        <Link to={campaign.entryPath(session.id)}>{session.title}</Link> <span className="muted small">{realDate(fieldOf(session, "date"))}</span>
      </h3>
      {summary?.text.trim() ? <Markdown text={summary.text} references={summary.references} entryPath={campaign.entryPath} /> : <p className="muted">Noch ohne Zusammenfassung.</p>}
    </article>
  );
}
